import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
    console.error("Missing Supabase environment variables")
}

// Fallback to avoid crash if env vars are missing (e.g. build time mismatch)
// The app will load but data fetching will fail, which is better than a white screen.
const fallbackUrl = "https://placeholder.supabase.co"
const fallbackKey = "placeholder-key"

import { Capacitor, registerPlugin } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

export interface ShuvoSharedStorePlugin {
    getSessionToken(): Promise<{ token?: string, refreshToken?: string, userId?: string }>;
}
const ShuvoBridge = registerPlugin<ShuvoSharedStorePlugin>('ShuvoSharedStore');

const customStorageAdapter = {
    getItem: async (key: string): Promise<string | null> => {
        try {
            // 1. Try Native Preferences
            const { value } = await Preferences.get({ key })
            let sessionJson = value

            // 2. Fallback to localStorage if Preferences is empty
            if (!sessionJson) {
                console.log(`⚠️ [AUTH] Native empty, falling back to legacy localStorage for key: ${key}`)
                sessionJson = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null
            } else {
                console.log(`✅ [AUTH] Session successfully loaded from Native Preferences`)
            }

            // 3. If iOS, try to read from ShuvoBridge to see if Share Extension rotated the token
            if (sessionJson && Capacitor.getPlatform() === 'ios') {
                try {
                    const bridgeData = await ShuvoBridge.getSessionToken()
                    if (bridgeData.token && bridgeData.refreshToken) {
                        const parsedSession = JSON.parse(sessionJson)
                        // If ShuvoBridge has a different token, it means Share Extension rotated it
                        if (parsedSession.access_token !== bridgeData.token) {
                            console.log(`✅ [AUTH] Synced rotated token from Share Extension into Main App!`)
                            parsedSession.access_token = bridgeData.token
                            parsedSession.refresh_token = bridgeData.refreshToken
                            sessionJson = JSON.stringify(parsedSession)
                        }
                    }
                } catch (bridgeError) {
                    console.error("Failed to read from ShuvoBridge", bridgeError)
                }
            }

            return sessionJson
        } catch (e) {
            console.error("customStorageAdapter getItem failed", e)
            return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null
        }
    },
    setItem: async (key: string, value: string): Promise<void> => {
        try {
            await Preferences.set({ key, value })
            if (typeof window !== 'undefined') window.localStorage.setItem(key, value)
        } catch (e) {
            console.error("customStorageAdapter setItem failed", e)
            if (typeof window !== 'undefined') window.localStorage.setItem(key, value)
        }
    },
    removeItem: async (key: string): Promise<void> => {
        try {
            await Preferences.remove({ key })
            if (typeof window !== 'undefined') window.localStorage.removeItem(key)
        } catch (e) {
            console.error("customStorageAdapter removeItem failed", e)
            if (typeof window !== 'undefined') window.localStorage.removeItem(key)
        }
    }
}

export const supabase = createClient(
    supabaseUrl || fallbackUrl,
    supabaseAnonKey || fallbackKey,
    {
        auth: {
            storage: customStorageAdapter,
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: true
        },
        global: {
            fetch: (url, options) => {
                const _options = options || {}
                _options.headers = new Headers(_options.headers || {})
                
                const urlString = url.toString()
                
                // Aggressively bypass iOS WKWebView heuristic caching for Database queries.
                // Do NOT apply to Edge Functions (/functions/v1/) as they reject Cache-Control via CORS.
                if (!urlString.includes('/functions/v1/')) {
                    _options.headers.set('Cache-Control', 'no-cache, no-store, must-revalidate')
                    _options.headers.set('Pragma', 'no-cache')
                    _options.headers.set('Expires', '0')
                }
                
                return fetch(url, { ..._options, cache: 'no-store' }).then(res => {
                    if (res.status === 401) {
                        console.log(`🚨 [AUTH DIAGNOSTIC] API 401 Unauthorized for URL:`, urlString)
                    }
                    return res
                })
            }
        }
    }
)
